import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'

import { OutputType } from 'app/constants/outputType'
import { CompanyDetailsModel } from 'app/models/companyDetailsModel'
import { UserModel } from 'app/models/userModel'

import { AdminIcon, AdminDateField, CheckboxField } from '@/modules/shared/components'
import { getCompanyTypeIcon } from 'app/services/company/companyIconResolver'
import { Dropdown } from '@/app/features/company-selection/Dropdown'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { AdminModal, AdminModalRow } from '../shared/AdminModal'

const SORT_BY_OPTIONS = [
  { name: 'synchronization-report.sort-by-date', type: 0 },
  { name: 'synchronization-report.sort-by-type', type: 1 },
  { name: 'synchronization-report.sort-by-company', type: 2 },
]

type Props = {
  companies: CompanyDetailsModel[]
  users: UserModel[]
  getCompanies: (tenantId?: string) => Promise<Array<CompanyDetailsModel>>
  getAvailableUsers: (companyId?: string) => Promise<Array<UserModel>>
  getSynchronizationReport: (
    companies: Array<string>,
    user: string,
    startDate: Moment,
    endDate: Moment,
    outputType: OutputType,
    sortBy: number,
    onlyUpdater: boolean,
  ) => any
  onClose: () => void
}

/**
 * Wydruk raportu synchronizacji - funkcyjny odpowiednik
 * `printout/synchronizationModal.tsx`. Filtr po użytkowniku był tam zakomentowany
 * od dawna, więc nie wrócił.
 */
export const SynchronizationModal: React.FC<Props> = ({
  companies,
  getCompanies,
  getAvailableUsers,
  getSynchronizationReport,
  onClose,
}) => {
  const { t } = useLocalize()

  const [isCsv, setIsCsv] = React.useState(false)
  const [onlyUpdater, setOnlyUpdater] = React.useState(false)
  const [dateFrom, setDateFrom] = React.useState<Moment>(
    moment().add(-1, 'months'),
  )
  const [dateTo, setDateTo] = React.useState<Moment>(moment())
  const [sortBy, setSortBy] = React.useState(0)
  const [selectedCompanies, setSelectedCompanies] = React.useState<string[]>([])

  React.useEffect(() => {
    getCompanies()
    getAvailableUsers(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const allCompanies = React.useMemo(() => {
    const result: CompanyDetailsModel[] = []
    for (const company of companies) {
      result.push({ ...company })
      for (const child of company.childCompanies ?? []) {
        result.push({ ...child })
      }
    }
    return result
  }, [companies])

  React.useEffect(() => {
    setSelectedCompanies(allCompanies.map((company) => String(company.id)))
  }, [allCompanies])

  return (
    <AdminModal
      title={t(
        'synchronization-report.generate-synchronization-report-title',
      )}
      onClose={onClose}
      onConfirm={() => {
        onClose()
        getSynchronizationReport(
          selectedCompanies,
          null,
          dateFrom,
          dateTo,
          isCsv ? OutputType.Csv : OutputType.Pdf,
          sortBy,
          onlyUpdater,
        )
      }}
    >
      <AdminModalRow label={t('synchronization-report.period')}>
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-w-[190px]">
            <AdminDateField
              withTime
              value={dateFrom}
              onChange={(date) => date && setDateFrom(moment(date))}
            />
          </div>
          <span>-</span>
          <div className="tw-w-[190px]">
            <AdminDateField
              withTime
              value={dateTo}
              onChange={(date) => date && setDateTo(moment(date))}
            />
          </div>
        </div>
      </AdminModalRow>

      <AdminModalRow label={t('synchronization-report.companies')}>
        <Dropdown
          compact
          multiple
          showSelectAll
          portalMenu
          searchPlaceholder={t(
            'administrationPanel-users.filter-find-placeholder',
          )}
          placeholder={t('administrationPanel-users.filter-companies')}
          options={allCompanies.map((company) => ({
            value: String(company.id),
            label: company.companyName,
            content: (
              <span className="tw-flex tw-items-center tw-gap-2">
                <AdminIcon
                  name={getCompanyTypeIcon(
                    company.type,
                    company.accountingOnly,
                  )}
                  className="tw-h-4 tw-w-4"
                />
                <span className="tw-flex tw-flex-col">
                  <span>{company.companyName}</span>
                  {company.tenant != null && (
                    <span className="tw-text-xs tw-opacity-70">
                      {company.tenant.companyName}
                    </span>
                  )}
                </span>
              </span>
            ),
          }))}
          value={selectedCompanies}
          onChange={setSelectedCompanies}
        />
      </AdminModalRow>

      <AdminModalRow label={t('synchronization-report.sort-by')}>
        <Dropdown
          compact
          portalMenu
          value={String(sortBy)}
          options={SORT_BY_OPTIONS.map((option) => ({
            value: String(option.type),
            label: t(option.name),
          }))}
          onChange={(value) => setSortBy(Number(value))}
        />
      </AdminModalRow>

      <div className="tw-flex tw-flex-col tw-gap-2">
        <CheckboxField
          id="isCsv"
          checked={isCsv}
          label={t('synchronization-report.isCsv')}
          onChange={() => setIsCsv((value) => !value)}
        />
        <CheckboxField
          id="onlyUpdater"
          checked={onlyUpdater}
          label={t('synchronization-report.onlyUpdater')}
          onChange={() => setOnlyUpdater((value) => !value)}
        />
      </div>
    </AdminModal>
  )
}
